import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { Checkbox } from '@contrast-security-inc/design-system-inputs'
import { isKeyValueSet, isKeyValueGroupSet } from './utils'
import { FilterState, KeyValueListItemType, KeyValuesFilterValues } from '../../../../../types'
import { parentLabelCss } from '../../common'
import { ExpandableList, ListSearchInput, useListSearch } from '../../../layout'
import { EmptyFilterComponent, FilterProps } from '../../filters'
import { OnInputChangeHandler } from '@contrast-security-inc/design-system-inputs/dist/types/shared'
import { CheckLabel } from '../../check-label'

export type KeyValueFilters = Record<string, KeyValuesFilterValues>

const getFilterStatesFromGroup = (
  list: KeyValueListItemType[],
  key: string,
  checkState?: boolean
): FilterState[] | undefined => {
  return list
    ?.find((it) => it.id === key)
    ?.values?.reduce<
      FilterState[]
    >((acc, { value }) => [...acc, { id: key, value, flag: checkState }], [])
}

export interface FilterKeyValueProps extends Omit<FilterProps, 'filters' | 'resetFilter'> {
  filters: KeyValueFilters
}

// this is specific to application metadata response but
// could be viewed as generic if we follow its interface
export const FilterKeyValue = ({
  filterDef: {
    filterMsgId,
    filterKey,
    useFilterList,
    options: { search = false, showAll = false, maxHeight } = {}
  },
  filters,
  updateFilter
}: FilterKeyValueProps) => {
  // state/hooks
  const intl = useIntl()
  const { data, isLoading } = useFilterList({ filters })
  const listItems = React.useMemo(
    () => (data?.data?.filters || []) as KeyValueListItemType[],
    [data?.data?.filters]
  )
  const {
    searchText,
    onChangeSearchText,
    searchedList: filtersList
  } = useListSearch({
    itemToString: (it) => it.label || it.value || '',
    subKey: 'values',
    listItems
  })
  const searchPlaceholder = intl.formatMessage(
    { id: 'placeholder_search' },
    // ts-ignore dynamic msg id
    { type: intl.formatMessage({ id: filterMsgId }) }
  )
  // events
  const onFilterChange: OnInputChangeHandler<boolean, HTMLInputElement> = React.useCallback(
    (event, checkState) => {
      const parentId = event.target.getAttribute('data-parent')
      const key = event.target.getAttribute('data-key') || ''
      const value = event.target.getAttribute('data-value') || ''
      const filterState = !parentId // only groups have a parentId set
        ? { id: key, value, flag: checkState } // single selection
        : getFilterStatesFromGroup(listItems, parentId, checkState) // group selection

      if (filterState) {
        updateFilter(filterKey, filterState)
      }
    },
    [filterKey, updateFilter, listItems]
  )
  const KeyValueListItem = React.useCallback(
    ({ parent, id, label, value, count }: KeyValueListItemType) => {
      const isGroup = !parent
      const { id: key } = parent || {}
      const checkLabel = parent ? <CheckLabel count={count} value={value} /> : label

      return (
        <Checkbox
          className={label ? parentLabelCss : undefined}
          data-parent={id}
          data-key={key}
          data-value={value}
          testId={`${filterKey}-${key}-${value}`}
          label={checkLabel}
          isDisabled={count === 0}
          onChange={onFilterChange}
          isChecked={
            isGroup
              ? isKeyValueGroupSet(filters[filterKey], id || '', listItems)
              : isKeyValueSet(filters[filterKey], key || '', value || '')
          }
        />
      )
    },
    [filters, filterKey, onFilterChange, listItems]
  )

  return (
    <div data-testid="filter-keyvalue">
      {search && (
        <div
          className={css`
            margin-bottom: 16px;
          `}
        >
          <ListSearchInput
            onChangeSearchText={onChangeSearchText}
            searchText={searchText}
            placeholder={searchPlaceholder}
          />
        </div>
      )}
      <div
        className={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <ExpandableList
          maxHeight={maxHeight}
          subKey="values"
          showAll={showAll}
          isLoading={isLoading}
          listItems={filtersList}
          EmptyListComponent={EmptyFilterComponent}
          // @ts-ignore, we know this is supported
          ItemComponent={KeyValueListItem}
        />
      </div>
    </div>
  )
}
